import React, { useMemo, useState } from 'react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Cell } from 'recharts';
import { TrendingUp, TrendingDown, Users, Target, Activity, UserPlus } from 'lucide-react';

const GroupAnalytics = ({ analytics, darkMode, groups = [], resumo, totalContatosUnicos = 0, contatosReconstruidos = false, crescimentoUnicos = null, metaGeral = 0, showExport = false }) => {
  const [showAllSize, setShowAllSize] = useState(false);
  const [showAllGrowth, setShowAllGrowth] = useState(false);
  const metrics = useMemo(() => {
    if (!groups.length) return null;

    const totalParticipantes = groups.reduce((sum, g) => sum + (g.total_participantes || 0), 0);
    const activeGroups = groups.filter(g => g.status !== false);
    const withGrowth = groups.filter(g => g.growth > 0);
    const withDecline = groups.filter(g => g.growth < 0);
    const totalGrowth = groups.reduce((sum, g) => sum + (g.growth || 0), 0);
    const totalEntradas = groups.reduce((sum, g) => sum + (g.entradas_7d || 0), 0);
    const totalSaidas = groups.reduce((sum, g) => sum + (g.saidas_7d || 0), 0);

    // Top 10 maiores
    const topBySize = [...activeGroups].sort((a, b) => (b.total_participantes || 0) - (a.total_participantes || 0)).slice(0, 5);

    // Top crescimento
    const topGrowth = [...activeGroups].filter(g => g.growth).sort((a, b) => (b.growth || 0) - (a.growth || 0)).slice(0, 5);

    // Quem mais perdeu
    const topDecline = [...activeGroups].filter(g => g.growth < 0).sort((a, b) => (a.growth || 0) - (b.growth || 0)).slice(0, 10);

    return {
      totalParticipantes,
      totalGrupos: activeGroups.length,
      totalGrowth,
      totalEntradas,
      totalSaidas,
      gruposCrescendo: withGrowth.length,
      gruposDeclinando: withDecline.length,
      topBySize,
      topGrowth,
      topDecline,
    };
  }, [groups]);

  // Dados de mensagens por dia (do analytics original)
  const dailyData = useMemo(() => {
    if (!analytics?.messagesByDay) return [];
    return analytics.messagesByDay.map(d => ({
      dia: new Date(d.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
      mensagens: parseInt(d.count)
    }));
  }, [analytics]);

  if (!metrics) return null;

  const cardBg = darkMode ? "bg-zinc-800 border-gray-700" : "bg-white border-gray-200";
  const textMain = darkMode ? "text-white" : "text-gray-900";
  const textMuted = darkMode ? "text-gray-400" : "text-gray-500";

  const exportImage = async (elementId, filename) => {
    const el = document.getElementById(elementId);
    if (!el) return;
    // Clonar o elemento pra não afetar a página
    const clone = el.cloneNode(true);
    // Remover botões do clone
    clone.querySelectorAll('.export-btn').forEach(b => b.remove());
    // Posicionar fora da tela pra capturar
    clone.style.position = 'absolute';
    clone.style.left = '-9999px';
    clone.style.top = '0';
    clone.style.padding = '24px';
    clone.style.minWidth = '600px';
    clone.style.width = el.offsetWidth + 'px';
    document.body.appendChild(clone);
    await new Promise(r => setTimeout(r, 200));
    const html2canvas = (await import('html2canvas')).default;
    const canvas = await html2canvas(clone, { 
      backgroundColor: darkMode ? '#18181b' : '#ffffff',
      useCORS: true,
      scale: 2,
    });
    document.body.removeChild(clone);
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url; a.download = filename + '.png'; a.click();
  };

  const exportGroupsCSV = () => {
    const rows = groups.map(g => {
      const catCols = (g.categorias_valores || []).reduce((acc, cv) => {
        const key = cv.categoria_label || cv.categoria_id || 'cat';
        acc[key] = acc[key] ? acc[key] + ', ' + cv.opcao_nome : cv.opcao_nome;
        return acc;
      }, {});
      return {
        nome: g.nome,
        cidade: g.cidade || '',
        bairro: g.bairro || '',
        total_participantes: g.total_participantes || 0,
        contatos_exclusivos: g.contatos_unicos || 0,
        crescimento_7d: g.growth || 0,
        ...catCols,
      };
    });
    if (!rows.length) return;
    const allHeaders = [...new Set(rows.flatMap(r => Object.keys(r)))];
    const summaryRow = allHeaders.map(h => {
      if (h === 'nome') return '"TOTAL (pessoas distintas)"';
      if (h === 'total_participantes') return '"' + rows.reduce((s, r) => s + (r.total_participantes || 0), 0) + '"';
      if (h === 'contatos_exclusivos') return '"' + totalContatosUnicos + '"';
      return '""';
    }).join(',');
    const csv = [allHeaders.join(','), ...rows.map(row => allHeaders.map(h => '"' + (row[h] ?? '') + '"').join(',')), summaryRow].join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'grupos_filtrados.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const ExportImgBtn = ({ elementId, filename }) => showExport ? (
    <button data-export-btn onClick={() => exportImage(elementId, filename)} className={`export-btn text-[10px] px-2 py-0.5 rounded ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
      📷
    </button>
  ) : null;

  return (
    <div className="space-y-4">
      {/* Export */}
      {showExport && (
        <div className="flex justify-end">
          <button onClick={exportGroupsCSV} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${darkMode ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
            📥 Exportar grupos filtrados (CSV)
          </button>
        </div>
      )}
      {/* Métricas Principais */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className={`rounded-lg border p-3 ${cardBg}`}>
          <div className="flex items-center gap-2 mb-1">
            <Users size={14} className="text-blue-500" />
            <span className={`text-xs ${textMuted}`}>Grupos Ativos</span>
          </div>
          <p className={`text-xl font-bold ${textMain}`}>{metrics.totalGrupos}</p>
        </div>
        <div className={`rounded-lg border p-3 ${cardBg}`}>
          <div className="flex items-center gap-2 mb-1">
            <Target size={14} className="text-green-500" />
            <span className={`text-xs ${textMuted}`}>Total Participantes</span>
          </div>
          <p className={`text-xl font-bold ${textMain}`}>{metrics.totalParticipantes.toLocaleString()}</p>
        </div>
        <div className={`rounded-lg border p-3 ${cardBg}`}>
          <div className="flex items-center gap-2 mb-1">
            <UserPlus size={14} className={(crescimentoUnicos?.crescimento ?? metrics.totalGrowth) >= 0 ? "text-green-500" : "text-red-500"} />
            <span className={`text-xs ${textMuted}`}>Crescimento de contatos únicos (7d)</span>
          </div>
          {crescimentoUnicos ? (
            <>
              <p className={`text-xl font-bold ${crescimentoUnicos.crescimento >= 0 ? "text-green-500" : "text-red-500"}`}>
                {crescimentoUnicos.crescimento > 0 ? '+' : ''}{crescimentoUnicos.crescimento.toLocaleString()}
              </p>
              <p className={`text-[11px] ${textMuted} mt-0.5`}>
                {crescimentoUnicos.inicial?.toLocaleString()} → {crescimentoUnicos.atual?.toLocaleString()} pessoas
                {crescimentoUnicos.estimado ? ' · série reconstruída' : ''}
                {crescimentoUnicos.filtrado ? ' · filtrado' : ''}
              </p>
            </>
          ) : (
            <p className={`text-xl font-bold ${metrics.totalGrowth >= 0 ? "text-green-500" : "text-red-500"}`}>
              {metrics.totalGrowth > 0 ? '+' : ''}{metrics.totalGrowth}
            </p>
          )}
        </div>
      {/*   <div className={`rounded-lg border p-3 ${cardBg}`}>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={14} className="text-blue-500" />
            <span className={`text-xs ${textMuted}`}>Movimentação (7d)</span>
          </div>
          <p className="text-xl font-bold">
            <span className="text-green-500">+{metrics.totalEntradas}</span>
            <span className={`text-sm font-normal ${textMuted}`}> entraram</span>
          </p>
          <p className={`text-[11px] ${textMuted} mt-0.5`}>
            <span className="text-red-500">−{metrics.totalSaidas} saíram</span> de grupos no período
          </p>
        </div> */}
        <div className={`rounded-lg border p-3 ${cardBg}`}>
          <div className="flex items-center gap-2 mb-1">
            <Activity size={14} className="text-purple-500" />
            <span className={`text-xs ${textMuted}`}>Contatos Únicos (total de pessoas)</span>
          </div>
          <p className={`text-xl font-bold text-purple-500`}>{totalContatosUnicos.toLocaleString()}</p>
          {contatosReconstruidos && (
            <p className={`text-[11px] ${textMuted} mt-0.5`}>
              📅 valor reconstruído para a data do filtro (estimativa)
            </p>
          )}
        </div>
      </div>
      <p className={`text-[11px] ${textMuted} -mt-2 mb-2`}>
        "Crescimento de contatos únicos" = quantas pessoas distintas a mais no período (cada
        pessoa conta uma vez). "Movimentação" = total de entradas e saídas de grupos (uma
        pessoa pode entrar/sair de vários grupos, por isso os números não se somam).
      </p>

      {/* Meta de Contatos */}
      {metaGeral > 0 && (
        <div className={`rounded-xl p-4 border ${cardBg}`}>
          <div className="flex items-center justify-between mb-2">
            <h3 className={`text-sm font-semibold ${darkMode ? "text-gray-200" : "text-gray-700"}`}>
              🎯 Meta de Contatos Únicos
            </h3>
            <span className={`text-xs ${textMuted}`}>
              {totalContatosUnicos.toLocaleString()} / {metaGeral.toLocaleString()}
            </span>
          </div>
          <div className={`w-full h-3 rounded-full ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}>
            <div
              className="h-3 rounded-full bg-gradient-to-r from-blue-500 to-green-500 transition-all"
              style={{ width: `${Math.min(100, Math.round((totalContatosUnicos / metaGeral) * 100))}%` }}
            />
          </div>
          <p className={`text-xs mt-1 ${textMuted}`}>
            {Math.round((totalContatosUnicos / metaGeral) * 100)}% da meta alcançada
          </p>
        </div>
      )}

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Mensagens por dia */}
        {dailyData.length > 0 && (
          <div id="chart-atividade" className={`rounded-lg border p-4 ${cardBg}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold ${textMain}`}>Atividade (mensagens/dia)</h3>
              <ExportImgBtn elementId="chart-atividade" filename="atividade_mensagens" />
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={dailyData} margin={{ top: 20, right: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#e5e7eb"} />
                <XAxis dataKey="dia" stroke={darkMode ? "#9ca3af" : "#6b7280"} style={{ fontSize: '10px' }} />
                <YAxis stroke={darkMode ? "#9ca3af" : "#6b7280"} style={{ fontSize: '10px' }} />
                <Tooltip contentStyle={{ backgroundColor: darkMode ? "#1f2937" : "#fff", border: `1px solid ${darkMode ? "#374151" : "#e5e7eb"}`, borderRadius: "8px", fontSize: '12px' }} />
                <Line type="monotone" dataKey="mensagens" stroke="#10b981" strokeWidth={2} dot={{ r: 3, fill: '#10b981' }}>
                  <LabelList dataKey="mensagens" position="top" style={{ fontSize: '9px', fill: darkMode ? '#9ca3af' : '#6b7280' }} />
                </Line>
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Top grupos por tamanho */}
        <div id="chart-maiores" className={`rounded-lg border p-4 ${cardBg}`}>
          <div className="flex items-center justify-between mb-3">
            <h3 className={`text-sm font-semibold ${textMain}`}>Maiores Grupos</h3>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowAllSize(!showAllSize)} className={`text-[10px] px-2 py-0.5 rounded ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {showAllSize ? 'Top 5' : 'Ver todos'}
              </button>
              <ExportImgBtn elementId="chart-maiores" filename="maiores_grupos" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={showAllSize ? Math.max(220, groups.filter(g => g.status !== false).length * 28 + 10) : 220}>
            <BarChart data={(showAllSize ? [...groups].filter(g => g.status !== false).sort((a, b) => (b.total_participantes || 0) - (a.total_participantes || 0)) : metrics.topBySize).map(g => ({ nome: g.nome, participantes: g.total_participantes }))} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#e5e7eb"} />
              <XAxis type="number" stroke={darkMode ? "#9ca3af" : "#6b7280"} style={{ fontSize: '10px' }} />
              <YAxis type="category" dataKey="nome" width={200} stroke={darkMode ? "#9ca3af" : "#6b7280"} style={{ fontSize: '10px' }} />
              <Tooltip contentStyle={{ backgroundColor: darkMode ? "#1f2937" : "#fff", border: `1px solid ${darkMode ? "#374151" : "#e5e7eb"}`, borderRadius: "8px", fontSize: '12px' }} />
              <Bar dataKey="participantes" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                <LabelList dataKey="participantes" position="insideRight" style={{ fontSize: '11px', fill: '#fff', fontWeight: 'bold' }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Crescimento e Declínio */}
      {(metrics.topGrowth.length > 0 || metrics.topDecline.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Crescendo */}
          {metrics.topGrowth.length > 0 && (
            <div id="chart-crescimento" className={`rounded-lg border p-4 ${cardBg}`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-sm font-semibold flex items-center gap-1.5 ${textMain}`}>
                  <TrendingUp size={14} className="text-green-500" /> Crescimento (7d)
                </h3>
                <div className="flex items-center gap-2">
                  <button onClick={() => setShowAllGrowth(!showAllGrowth)} className={`text-[10px] px-2 py-0.5 rounded ${darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {showAllGrowth ? 'Top 5' : 'Ver todos'}
                  </button>
                  <ExportImgBtn elementId="chart-crescimento" filename="mais_cresceram" />
                </div>
              </div>
              <ResponsiveContainer width="100%" height={showAllGrowth ? Math.max(220, groups.filter(g => g.growth).length * 28 + 10) : metrics.topGrowth.length * 36 + 10}>
                <BarChart data={(showAllGrowth ? [...groups].filter(g => g.growth).sort((a, b) => (b.growth || 0) - (a.growth || 0)) : metrics.topGrowth).map(g => ({ nome: g.nome, crescimento: g.growth }))} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? "#374151" : "#e5e7eb"} />
                  <XAxis type="number" stroke={darkMode ? "#9ca3af" : "#6b7280"} style={{ fontSize: '10px' }} />
                  <YAxis type="category" dataKey="nome" width={200} stroke={darkMode ? "#9ca3af" : "#6b7280"} style={{ fontSize: '10px' }} />
                  <Tooltip />
                  <Bar dataKey="crescimento" radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="crescimento" position="insideRight" style={{ fontSize: '11px', fill: '#fff', fontWeight: 'bold' }} />
                    {(showAllGrowth ? [...groups].filter(g => g.growth).sort((a, b) => (b.growth || 0) - (a.growth || 0)) : metrics.topGrowth).map((g, idx) => (
                      <Cell key={idx} fill={g.growth >= 0 ? '#22c55e' : '#ef4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Perdendo membros */}
          {metrics.topDecline.length > 0 && (
            <div className={`rounded-lg border p-4 ${cardBg}`}>
              <h3 className={`text-sm font-semibold mb-2 flex items-center gap-1.5 ${textMain}`}>
                <TrendingDown size={14} className="text-red-500" /> Perdendo membros (7d)
              </h3>
              <div className="space-y-1.5">
                {metrics.topDecline.map(g => (
                  <div key={g.id} className="flex items-center justify-between gap-2">
                    <span className={`text-xs ${textMuted}`}>{g.nome}</span>
                    <span className="text-xs font-semibold text-red-500 whitespace-nowrap">{g.growth}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GroupAnalytics;
